import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Toasts from "../../../core/common/toast/index";
import {
  clearNocData,
  clearNocError,
  fetchNocData,
  fetchNocDnARDropdownData,
} from "../../../core/data/redux/sapWorklistSlice";
import { NocOutsideJobApproval } from "./nocApprovals/NocOutsideJobApproval";
import { NocPassportApproval } from "./nocApprovals/NocPassportApproval";
import { NocHigherStudyApproval } from "./nocApprovals/NocHigherStudyApproval";
import { NocExIndiaApproval } from "./nocApprovals/NocExIndiaApproval";
import { NocOnlineCourseApproval } from "./nocApprovals/NocOnlineCourseApproval";
import { NocOnlineCourseReimbApproval } from "./nocApprovals/NocOnlineCourseReimbApproval";
import { VpfApproval } from "./nocApprovals/VpfApproval";
import { ToastState, DnarOption } from "./nocApprovals/common/types";
import { formatErrorMessage } from "./nocApprovals/common/nocUtils";

const TaskDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const rawType = (searchParams.get("type") || "").trim();
  const nocType = (rawType as
    | "NOC_OUTJOB"
    | "NOC_EXINDIA"
    | "NOC_HIGHERSTUDY"
    | "NOC_PASSPORT"
    | "NOC_ONLINECOURSE-REIMB"
    | "NOC_ONLINECOURSE") || "NOC_OUTJOB";

  const isVpf = rawType.toUpperCase().includes("VPF");

  // Redux state
  const { nocData, nocDropdownData, nocLoading, nocError, worklistData } = useSelector(
    (state: any) => state.sapWorklistState
  );

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [toast, setToast] = useState<ToastState>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  const showToast = (
    title: string,
    message: string,
    type: "success" | "danger" | "warning" | "primary"
  ) => {
    const formattedMessage = formatErrorMessage(message);
    setToast({
      show: true,
      title,
      message: formattedMessage,
      type,
    });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  // Redirect for self-transfer
  useEffect(() => {
    const lowerType = rawType.toLowerCase();
    if (
      lowerType.includes("self_transfer") ||
      lowerType.includes("transfer_posting") ||
      lowerType === "transfer"
    ) {
      navigate(`/todo-worklist/transfer-posting-approval/${encodeURIComponent(String(id || ""))}`);
    }
  }, [rawType, id, navigate]);

  // Fetch NOC data
  useEffect(() => {
    if (id && !isVpf) {
      setIsInitialLoading(true);
      dispatch(fetchNocData({ requestID: id, type: nocType })).finally(() => {
        setIsInitialLoading(false);
      });
    } else {
      setIsInitialLoading(false);
    }

    return () => {
      dispatch(clearNocData());
      dispatch(clearNocError());
    };
  }, [id, nocType, dispatch, isVpf]);

  // Fetch D&AR dropdown options
  useEffect(() => {
    if (!isVpf) {
      dispatch(fetchNocDnARDropdownData());
    }
  }, [dispatch, isVpf]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    if (id && !isVpf) {
      setIsInitialLoading(true);
      dispatch(fetchNocData({ requestID: id, type: nocType })).finally(() => {
        setIsInitialLoading(false);
      });
    }
  };

  // Transform D&AR master list
  const dnarMasterList: DnarOption[] = Array.isArray(nocDropdownData)
    ? nocDropdownData.map((item: any) => ({
        dnar_id: item.dnar_id || item.value || item.id,
        dnar_title: item.dnar_title || item.label || item.title,
        value: item.dnar_id || item.value || item.id,
        label: item.dnar_title || item.label || item.title,
      }))
    : [];

  // VPF item if applicable
  const vpfItem = isVpf
    ? worklistData?.data?.find(
        (item: any) => item.id?.toString() === id || item.requestID?.toString() === id
      )
    : null;

  // Handle VPF
  if (isVpf) {
    return (
      <>
        <VpfApproval
          id={id || ""}
          nocData={nocData}
          vpfItem={vpfItem}
          onRefresh={handleRefresh}
          onBack={handleBack}
          onShowToast={showToast}
        />
        <Toasts
          show={toast.show}
          title={toast.title}
          message={toast.message}
          type={toast.type}
        />
      </>
    );
  }

  // Loading State
  if (isInitialLoading || (nocLoading && !nocData)) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="mt-3 text-muted">Loading Application Details...</h5>
              <p className="text-muted small">Please wait while we fetch the information.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / No Data State
  if (!nocData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="card border-0 shadow-sm mt-5">
                <div className="card-body text-center py-5">
                  <i className="ti ti-file-x display-4 text-muted mb-3 d-block"></i>
                  <h5 className="text-dark fw-bold">No Application Data Found</h5>
                  <p className="text-muted">
                    {nocError || "We could not locate the details for this NOC request."}
                  </p>
                  <div className="d-flex justify-content-center gap-2 mt-4">
                    <button onClick={handleRefresh} className="btn btn-outline-primary">
                      <i className="ti ti-refresh me-2"></i>Retry
                    </button>
                    <button onClick={handleBack} className="btn btn-primary">
                      <i className="ti ti-arrow-left me-2"></i>Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render the Dedicated NOC Approver Component according to nocType
  const renderNocComponent = () => {
    switch (nocType) {
      case "NOC_PASSPORT":
        return (
          <NocPassportApproval
            id={id || ""}
            nocData={nocData}
            onRefresh={handleRefresh}
            onBack={handleBack}
            dnarMasterList={dnarMasterList}
            onShowToast={showToast}
          />
        );
      case "NOC_HIGHERSTUDY":
        return (
          <NocHigherStudyApproval
            id={id || ""}
            nocData={nocData}
            onRefresh={handleRefresh}
            onBack={handleBack}
            dnarMasterList={dnarMasterList}
            onShowToast={showToast}
          />
        );
      case "NOC_EXINDIA":
        return (
          <NocExIndiaApproval
            id={id || ""}
            nocData={nocData}
            onRefresh={handleRefresh}
            onBack={handleBack}
            dnarMasterList={dnarMasterList}
            onShowToast={showToast}
          />
        );
      case "NOC_ONLINECOURSE":
        return (
          <NocOnlineCourseApproval
            id={id || ""}
            nocData={nocData}
            onRefresh={handleRefresh}
            onBack={handleBack}
            onShowToast={showToast}
          />
        );
      case "NOC_ONLINECOURSE-REIMB":
        return (
          <NocOnlineCourseReimbApproval
            id={id || ""}
            nocData={nocData}
            onRefresh={handleRefresh}
            onBack={handleBack}
            onShowToast={showToast}
          />
        );
      case "NOC_OUTJOB":
      default:
        return (
          <NocOutsideJobApproval
            id={id || ""}
            nocData={nocData}
            onRefresh={handleRefresh}
            onBack={handleBack}
            dnarMasterList={dnarMasterList}
            onShowToast={showToast}
          />
        );
    }
  };

  return (
    <>
      {renderNocComponent()}
      <Toasts
        show={toast.show}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </>
  );
};

export default TaskDetailsPage;
